import { computed, ref } from 'vue'

import { separatedGeolocationCodec } from '../domain/geolocationCodecs'
import {
  type LocationGroup,
  type LocationGroupDraft,
  normalizeLocationGroupDraft,
  validateLocationGroupDraft
} from '../domain/locationGroup'

export type LocationGroupEditorMode = 'create' | 'edit'

export function useLocationGroupEditor() {
  const mode = ref<LocationGroupEditorMode>('create')
  const editedGroupId = ref<string | null>(null)
  const name = ref('')
  const available = ref('')
  const unavailable = ref('')
  const submitted = ref(false)

  const parsedAvailable = computed(() => separatedGeolocationCodec.parse(available.value, 'AVAILABLE'))
  const parsedUnavailable = computed(() => separatedGeolocationCodec.parse(unavailable.value, 'UNAVAILABLE'))
  const geolocations = computed(() => [...parsedAvailable.value.geolocations, ...parsedUnavailable.value.geolocations])
  const hasInvalidLocations = computed(
    () => parsedAvailable.value.invalidLines.length > 0 || parsedUnavailable.value.invalidLines.length > 0
  )
  const nameError = computed(() => (submitted.value && !name.value.trim() ? 'locationGroups.validation.name' : ''))
  const locationsError = computed(() => {
    if (!submitted.value) return ''
    if (hasInvalidLocations.value) return 'locationGroups.validation.invalidLocations'
    return geolocations.value.length ? '' : 'locationGroups.validation.locationsRequired'
  })

  function reset(group?: LocationGroup): void {
    mode.value = group ? 'edit' : 'create'
    editedGroupId.value = group?.id ?? null
    name.value = group?.name ?? ''
    available.value = group ? separatedGeolocationCodec.format(group.geolocations, 'AVAILABLE') : ''
    unavailable.value = group ? separatedGeolocationCodec.format(group.geolocations, 'UNAVAILABLE') : ''
    submitted.value = false
  }

  function buildDraft(): LocationGroupDraft | null {
    submitted.value = true
    const draft = normalizeLocationGroupDraft({ name: name.value, geolocations: geolocations.value })
    const validation = validateLocationGroupDraft(draft)
    if (nameError.value || locationsError.value || validation.name || validation.geolocations) return null
    return draft
  }

  return {
    mode,
    editedGroupId,
    name,
    available,
    unavailable,
    geolocations,
    nameError,
    locationsError,
    reset,
    buildDraft
  }
}
